import { CloseIcon, DropdownArrowDownIcon, DropdownArrowUpIcon } from "../../../icons/monochrome";
import classNames from "classnames";
import styles from "./Select.module.scss";
import { ThreeDotLoader } from "../../index";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import uuidv4 from "uuid/v4";
import Menu from "./Menu";
import { SelectedOptionRenderer, MultiSelectedOptionsRenderer } from "./OptionsRenderers";

const valueToState = (value, isMulti) => {
    const valuesSet = new Set();

    if (!value || (isMulti && _.isArray(value) && value?.length === 0)) {
        return null;
    }

    const valueAsArray = _.isArray(value) ? value : [value];

    if (isMulti) {
        _.forEach(valueAsArray, v => {
            valuesSet.add(v.value);
        })
    } else {
        valuesSet.add(valueAsArray[0].value);
    }

    return {
        selectedOptions: valueAsArray,
        valuesSet
    };
}

const useStateManager = (componentId, isRequireAcceptOnChange, value, isMulti, onChange, name, isMenuOpen) => {
    const stateRef = useRef(null);
    const [internalValue, setInternalValue] = useState(isRequireAcceptOnChange ? value : undefined);

    const selectionState = useMemo(() => {
        return valueToState(value, isMulti);
    }, [value, isMulti]);

    const internalSelectionState = useMemo(() => {
        if (!isRequireAcceptOnChange) {
            return null;
        }

        return valueToState(internalValue, isMulti);
    }, [internalValue, isMulti, isRequireAcceptOnChange]);

    stateRef.current = {
        selectionState,
        internalSelectionState,
        internalValue,
        value
    };

    const updateValue = useCallback((newValueOrFunction) => {
        const { internalSelectionState, selectionState: externalSelectionState } = stateRef.current;
        const selectionState = isRequireAcceptOnChange ? internalSelectionState : externalSelectionState;
        const newValue = _.isFunction(newValueOrFunction) ? newValueOrFunction(selectionState) : newValueOrFunction;

        if (isRequireAcceptOnChange) {
            setInternalValue(newValue);
        } else {
            onChange(newValue, { name })
        }
    }, [onChange, name, isRequireAcceptOnChange])

    const acceptChanges = useCallback(() => {
        onChange(stateRef.current.internalValue, { name })
    }, [onChange, name]);

    useEffect(() => {
        if (isMenuOpen) {
            setInternalValue(stateRef.current.value);
        }
    }, [isMenuOpen]);

    const clearAll = useCallback(e => {
        updateValue(null)
        e.__multiSelectClear = componentId.current;
    }, [updateValue, componentId]);

    const toggleOption = useCallback((option) => {
        updateValue(selectionState => {
            const isSelected = selectionState?.valuesSet.has(option.value) || false;

            if (isMulti) {
                if (isSelected) {
                    return _.reject(selectionState.selectedOptions, { value: option.value });
                } else {
                    if (!selectionState) {
                        return [option];
                    } else {
                        return selectionState.selectedOptions.concat(option);
                    }
                }
            } else {
                return option;
            }
        })
    }, [updateValue, isMulti]);

    const unselectOption = useCallback((option) => {
        if (isMulti) {
            updateValue(selectionState => _.reject(selectionState.selectedOptions, { value: option.value }));
        } else {
            updateValue(selectionState => selectionState?.valuesSet.has(option.value) ? null : selectionState.selectedOptions);
        }
    }, [updateValue, isMulti]);

    const createOption = useCallback(value => {
        const existingOption = _.find(stateRef.current.selectionState?.selectedOptions, { value });

        if(existingOption) {
            //if option with same value exists, toggle it only if its not currently selected
            if(!stateRef.current.selectionState?.valuesSet.has(value)) {
                toggleOption(existingOption)
            }
        } else {
            toggleOption({ value, label: value, _isNew: true })
        }
    }, [toggleOption]);

    return { selectionState, internalSelectionState, toggleOption, unselectOption, clearAll, acceptChanges, createOption }
}

const Select = React.memo(({
                                        placeholder,
                                        value,
                                        isLoading,
                                        showSecondaryLabelInSelectedOption,
                                        isDisabled,
                                        options,
                                        isClearable,
                                        onChange,
                                        name,
                                        hasError,
                                        menuPortalTarget,
                                        isSearchable,
                                        isMulti,
                                        noOptionsMessage,
                                        isRequireAcceptOnChange,
                                        isCreatable
                                    }) => {
    const componentId = useRef(uuidv4());
    const [isMenuOpen, setMenuOpen] = useState(false);
    const controlRef = useRef(null);
    const { selectionState, internalSelectionState, toggleOption, unselectOption, clearAll, acceptChanges, createOption } = useStateManager(componentId, isRequireAcceptOnChange, value, isMulti, onChange, name, isMenuOpen);

    const handleKeyDown = (isDisabled || isLoading) ? undefined : e => {
        if (e.key === 'Enter' || e.key === 'Backspace') {
            setMenuOpen(isOpen => !isOpen);
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const onClick = useCallback((e) => {
        if (e.__multiSelectClose === componentId.current || e?.nativeEvent?.__multiSelectClose === componentId.current ||
            e.__multiSelectRemove || e?.nativeEvent.__multiSelectRemove ||
            e.__multiSelectClear || e?.nativeEvent.__multiSelectClear
        ) {
            return;
        }

        setMenuOpen(isOpen => !isOpen);
        e.stopPropagation();
        e.preventDefault();
    }, [setMenuOpen]);

    return <>
        <div className={classNames(styles["control"], { [styles["invalid"]]: hasError, [styles["disabled"]]: isDisabled, [styles["open"]]: isMenuOpen })} onClick={(isDisabled || isLoading) ? undefined : onClick} ref={controlRef}
             tabIndex={isDisabled ? -1 : 0}
             onKeyDown={handleKeyDown}>
            {!selectionState && <div className={styles["placeholder"]}>{placeholder || "Select..."}</div>}
            {isLoading && <ThreeDotLoader className={styles["loading"]}/>}
            {selectionState && !isMulti && <SelectedOptionRenderer
                selectionState={selectionState}
                showSecondaryLabelInSelectedOption={showSecondaryLabelInSelectedOption}/>}
            {selectionState && isMulti && <MultiSelectedOptionsRenderer
                unselectOption={unselectOption}
                toggleOption={toggleOption}
                name={name}
                selectionState={selectionState}
                showSecondaryLabelInSelectedOption={showSecondaryLabelInSelectedOption}
                isRequireAcceptOnChange={isRequireAcceptOnChange}
            />}
            {!isLoading && <div className={styles["controls"]}>
                {isClearable && selectionState && <button onClick={clearAll}><CloseIcon/></button>}
                {isMenuOpen ? <DropdownArrowUpIcon/> : <DropdownArrowDownIcon/>}
            </div>}
        </div>
        {isMenuOpen && <Menu noOptionsMessage={noOptionsMessage}
                             options={options}
                             name={name}
                             unselectOption={unselectOption}
                             toggleOption={toggleOption}
                             selectionState={isRequireAcceptOnChange ? internalSelectionState : selectionState}
                             controlRef={controlRef}
                             setMenuOpen={setMenuOpen}
                             menuPortalTarget={menuPortalTarget}
                             isSearchable={isSearchable}
                             isCreatable={isCreatable}
                             isMulti={isMulti}
                             componentId={componentId}
                             isRequireAcceptOnChange={isRequireAcceptOnChange}
                             acceptChanges={acceptChanges}
                             createOption={createOption}
        />}
    </>
})

export default Select;